import { useEffect, useState } from 'react';
import type { Tenant } from '@provalia/shared';
import { Card, ErrorBox, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { TenantForm, draftFromTenant, validateTenant, type TenantErrors } from '../../components/TenantForm';
import type { TenantTabProps } from '../TenantDetail';
import { CompaniesCard } from './CompaniesCard';
import { UdfCard } from './UdfCard';

export function ConnectionTab(props: TenantTabProps): JSX.Element {
  return (
    <div className="stack lg">
      <TenantEditCard {...props} />
      <CompaniesCard {...props} />
      <UdfCard {...props} />
    </div>
  );
}

function TenantEditCard({ slug, detail, reload }: TenantTabProps): JSX.Element {
  const toast = useToast();
  const { tenant } = detail;
  const [draft, setDraft] = useState(() => draftFromTenant(tenant));
  const [errors, setErrors] = useState<TenantErrors>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const original = draftFromTenant(tenant);
  const originalKey = JSON.stringify(original);
  const dirty = originalKey !== JSON.stringify(draft);

  // Solo se reinicia el formulario si lo guardado cambió de verdad, no en cada recarga de la ficha.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDraft(JSON.parse(originalKey) as typeof original), [originalKey]);
  const domain = new URL(tenant.portalUrl).host.replace(/^[^.]+\./, '');

  const save = async (): Promise<void> => {
    const { input, errors: found } = validateTenant(draft);
    setErrors(found);
    if (!input) return;
    setBusy(true);
    setError(null);
    try {
      await api.put<{ tenant: Tenant }>(tenantPath(slug), input);
      toast('Conexión guardada', 'ok');
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Conexión con el Service Layer">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <TenantForm draft={draft} onChange={setDraft} errors={errors} mode="edit" domain={domain} />
        <ErrorBox error={error} />
        <div className="row end">
          {dirty ? (
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setDraft(original);
                setErrors({});
                setError(null);
              }}
              disabled={busy}
            >
              Descartar cambios
            </button>
          ) : null}
          <button type="submit" className="btn primary" disabled={busy || !dirty}>
            {busy ? 'Guardando…' : 'Guardar conexión'}
          </button>
        </div>
      </form>
    </Card>
  );
}
